import React, { useEffect } from "react";
import {
  Stack,
  Typography,
  styled,
  IconButton,
  MenuItem,
  Menu,
  Button,
  Skeleton,
  CircularProgress,
  Box,
} from "@mui/material";
import Chat from "../../assets/images/UserSession.svg";
import ChatPink from "../../assets/images/UserSessionPink.svg";
import ThreeDots from "../../assets/images/threedotsOrange.svg";
import Rename from "../../assets/images/Rename.svg";
import Delete from "../../assets/images/Delete.svg";
import { useSelector, useDispatch } from "react-redux";
import { getUserChats } from "../../redux/slice/get-user-chats";
import DeleteDialog from "../DeleteDialog/DeleteDialog";
import { deleteUserSession } from "../../redux/slice/delete-user-session";
import RenameDialog from "../RenameDialog/RenameDialog";
import moment from "moment";
import Loader from "../Loader";
import ScrollingText from "../ScrollingText/ScrollingText";
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary";
import { getUserSessions } from "../../redux/slice/get-user-sessions";
import "./HistoryTab.scss";
import {
  filteredStateUpdate,
  incrementNoOfDays,
  initializeNoOfDays,
  newChatStateUpdate,
  resetMessages,
  setActiveSessionId,
  setActiveChatToolId,
} from "../../redux/slice/snackbar";

const DataList = styled(Typography)({
  fontSize: "14px",
  flex: 1,
  minWidth: 0,
});
const StackStyle = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ active }) => ({
  padding: "9px",
  borderRadius: "8px",
  color: active ? "#E56800" : "inherit",

  "& .horizon-icon-button-holder img": {
    display: "none",
  },
  "& .chat-icon": {
    display: active ? "none" : "inline",
  },
  "& .chat-icon-pink": {
    display: active ? "inline" : "none",
    marginLeft: "0px",
  },
  "&:hover": {
    color: "#E56800",
    cursor: "pointer",
    background: "#E8E8E8",
    paddingRight: "0px",
    borderRadius: "8px",
    "& .chat-icon-pink": {
      display: "inline",
      marginLeft: "0px",
    },
    "& .chat-icon": {
      display: "none",
    },
    "& .horizon-icon-button-holder": {
      marginLeft: "auto",
    },
    "& .horizon-icon-button-holder img": {
      display: "inline-block",
    },
  },
}));

const sortByDateTime = (data) => {
  return data.sort((a, b) => {
    const dateA = moment(a.updated_at);
    const dateB = moment(b.updated_at);

    return dateB - dateA; // Descending order
  });
};

// Function to categorize the data
const categorizeData = (data) => {
  const categories = {
    today: [],
    yesterday: [],
    past7Days: [],
    lastMonth: [],
    months: {}, // This will contain year and month-wise data
  };

  const now = moment();
  const startOfToday = now.startOf("day");
  const startOfYesterday = startOfToday.clone().subtract(1, "day");
  const startOf7DaysAgo = startOfToday.clone().subtract(7, "days");
  const startOfLastMonth = now.clone().subtract(1, "month").startOf("month");
  const endOfLastMonth = now.clone().subtract(1, "month").endOf("month");

  data?.forEach((item) => {
    const dateModified = moment(item.updated_at);

    if (dateModified.isSame(startOfToday, "day")) {
      categories.today.push(item);
    } else if (dateModified.isSame(startOfYesterday, "day")) {
      categories.yesterday.push(item);
    } else if (
      dateModified.isAfter(startOf7DaysAgo) &&
      dateModified.isBefore(startOfToday)
    ) {
      categories.past7Days.push(item);
    } else if (
      dateModified.isBetween(startOfLastMonth, endOfLastMonth, "day", "[]")
    ) {
      categories.lastMonth.push(item);
    } else {
      const year = dateModified.year();
      const month = dateModified.format("MMMM YYYY");
      if (!categories.months[year]) {
        categories.months[year] = {};
      }
      if (!categories.months[year][month]) {
        categories.months[year][month] = [];
      }
      categories.months[year][month].push(item);
    }
  });

  // Sort months within each year in ascending order
  categories.months = Object.keys(categories.months).reduce(
    (sortedYears, year) => {
      sortedYears[year] = Object.keys(categories.months[year])
        .sort((a, b) => moment(a, "MMMM YYYY").diff(moment(b, "MMMM YYYY")))
        .reduce((sortedMonths, month) => {
          sortedMonths[month] = categories.months[year][month];
          return sortedMonths;
        }, {});
      return sortedYears;
    },
    {},
  );

  return categories;
};

// Flattens the categorized data into the ordered list of groups to render.
const buildSessionGroups = (categorizedData) => [
  { title: "Today", items: categorizedData.today },
  { title: "Yesterday", items: categorizedData.yesterday },
  { title: "Past 7 Days", items: categorizedData.past7Days },
  { title: "Last Month", items: categorizedData.lastMonth },
  ...Object.keys(categorizedData.months || {})
    .sort((a, b) => parseInt(b, 10) - parseInt(a, 10)) // Newest year first
    .flatMap((year) =>
      Object.keys(categorizedData.months[year]).map((month) => ({
        title: month,
        items: categorizedData.months[year][month],
      })),
    ),
];

function HistorySessionListSkeleton({ rows = 6 }) {
  return (
    <Stack spacing={0.5} className="history-sessions-skeleton" aria-busy="true">
      {Array.from({ length: rows }).map((_, index) => (
        <Stack
          key={index}
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ px: "9px", py: "9px" }}
        >
          <Skeleton variant="circular" width={20} height={20} animation="wave" />
          <Skeleton
            variant="text"
            height={18}
            animation="wave"
            sx={{ flex: 1, maxWidth: `${55 + (index % 3) * 12}%` }}
          />
        </Stack>
      ))}
    </Stack>
  );
}

function SessionsLoadError({ message, onRetry }) {
  return (
    <Stack spacing={1} alignItems="flex-start" sx={{ px: 1, py: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
      <Button size="small" onClick={onRetry} sx={{ color: "#E56800" }}>
        Try again
      </Button>
    </Stack>
  );
}

function SessionListItem({
  data,
  active,
  onSelect,
  onMenuOpen,
  menuAnchorEl,
  menuSessionId,
  onMenuClose,
  onRename,
  onDelete,
}) {
  return (
    <StackStyle
      spacing={2}
      direction="row"
      alignItems="center"
      className="history-tab-holder"
      active={active}
    >
      <img src={Chat} alt="chat" className="chat-icon" />
      <img src={ChatPink} alt="chat" className="chat-icon-pink" />
      <DataList
        component="div"
        onClick={() => onSelect(data.session_id, data.use_case)}
      >
        <ScrollingText text={data.session_name} />
      </DataList>
      <IconButton
        onClick={(event) => onMenuOpen(event, data)}
        className="horizon-icon-button-holder"
      >
        <img src={ThreeDots} alt="three-dots" />
      </IconButton>

      <Menu
        id="basic-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl) && menuSessionId === data.session_id}
        onClose={onMenuClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem onClick={() => onRename(data.session_id, data.session_name)}>
          <img src={Rename} alt="Rename icon" />
          &nbsp; Rename
        </MenuItem>
        <MenuItem onClick={() => onDelete(data.session_id, data.session_name)}>
          <img src={Delete} alt="Delete icon" />
          &nbsp; Delete
        </MenuItem>
      </Menu>
    </StackStyle>
  );
}

function SessionGroup({ title, items, activeSessionId, ...itemHandlers }) {
  if (!items || items.length === 0) return null;

  return (
    <React.Fragment>
      <Typography className="history-titles">{title}</Typography>
      {sortByDateTime(items).map((data) => (
        <SessionListItem
          key={data.session_id}
          data={data}
          active={activeSessionId === data.session_id}
          {...itemHandlers}
        />
      ))}
    </React.Fragment>
  );
}

export default function HistoryTab() {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = React.useState(false);
  const [userSessionId, setUserSessionId] = React.useState("");
  const [userSessionName, setUserSessionName] = React.useState("");
  const renameLoader = useSelector((state) => state.renameUser.isLoading);
  const [renameVal, setRenameVal] = React.useState("");
  const [menuSessionId, setMenuSessionId] = React.useState(null);

  const authUser = useSelector((state) => state.auth.user);
  const userEmail = authUser?.email || "";
  const token = "__cookie__";
  const client = "UT";
  const { minRows, maxRows, filtered, activeSessionId } = useSelector(
    (state) => state.snackbar,
  );
  const data = useSelector((state) => state.userSessions.userSessions);
  const sessionsLoading = useSelector((state) => state.userSessions.isLoading);
  const sessionsError = useSelector((state) => state.userSessions.error);
  const isInitialSessionsLoad = sessionsLoading && minRows === 0;
  const isLoadingMoreSessions = sessionsLoading && minRows > 0;
  const { deleteUser } = useSelector((state) => state.deleteUser);
  const [combinedSessions, setCombinedSessions] = React.useState([]);
  const [hasMoreSessions, setHasMoreSessions] = React.useState(false);
  const sessionsMountedRef = React.useRef(false);

  const fetchSessions = React.useCallback(
    (page) => {
      if (!userEmail) return;
      dispatch(
        getUserSessions({
          userEmail,
          token,
          client,
          minRows: page.minRows,
          maxRows: page.maxRows,
        }),
      );
    },
    [dispatch, userEmail, token, client],
  );

  useEffect(() => {
    if (!userEmail) return;

    const isFirstMount = !sessionsMountedRef.current;
    if (isFirstMount) {
      sessionsMountedRef.current = true;
      dispatch(initializeNoOfDays());
      fetchSessions({ minRows: 0, maxRows: 10 });
      return;
    }

    fetchSessions({ minRows, maxRows });
  }, [minRows, maxRows, userEmail, dispatch, fetchSessions]);

  useEffect(() => {
    // `data` is only an array on success; a failed page must not break the list.
    const pageSessions = Array.isArray(data) ? data : [];

    if (minRows === 0) {
      setCombinedSessions(pageSessions);
    } else if (pageSessions.length > 0) {
      setCombinedSessions((prev) => [
        ...(Array.isArray(prev) ? prev : []),
        ...pageSessions,
      ]);
    }
    setHasMoreSessions(pageSessions.length >= maxRows);
  }, [data, minRows, maxRows]);

  useEffect(() => {
    const uniqueSessions = Array.from(
      new Map(
        (Array.isArray(combinedSessions) ? combinedSessions : []).map((item) => [
          item.session_id,
          item,
        ]),
      ).values(),
    );

    const filteredSessions = deleteUser?.session_id
      ? uniqueSessions.filter(
          (item) => item.session_id !== deleteUser.session_id,
        )
      : uniqueSessions;

    dispatch(filteredStateUpdate(filteredSessions));
  }, [combinedSessions, deleteUser, dispatch]);

  const handleClick = (event, item) => {
    setAnchorEl(event.currentTarget);
    setMenuSessionId(item.session_id);
  };

  const handleRenameDialogClickOpen = () => {
    setRenameDialogOpen(true);
  };

  const handleRenameDialogClose = () => {
    setRenameDialogOpen(false);
  };
  const handleDeleteDialogClickOpen = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };
  const dispatchDeleteProcess = () => {
    const deletepayload = {
      session: userSessionId,
      userEmail: userEmail,
      token: token,
      client,
    };
    dispatch(deleteUserSession(deletepayload))
      .unwrap()
      .then(() => {
        dispatch(initializeNoOfDays());
      })
      .catch((err) => {
        console.error("Error:", err);
      });
    setDeleteDialogOpen(false);
    dispatch(resetMessages());
  };
  const handleClose = () => {
    setAnchorEl(null);
    setMenuSessionId(null);
  };
  const handleDeleteDialog = (id, name) => {
    setUserSessionName(name);
    handleDeleteDialogClickOpen();
    setUserSessionId(id);
    setAnchorEl(null);
  };
  const handleRenameDialog = (id, name) => {
    setRenameVal(name);
    handleRenameDialogClickOpen();
    setUserSessionId(id);
    setAnchorEl(null);
  };
  const handleRenamVal = (event) => {
    setRenameVal(event);
  };
  const handleuserSessionId = (sessionId, useCase) => {
    dispatch(setActiveSessionId(sessionId));
    dispatch(newChatStateUpdate(false));
    if (useCase === "knowledge-bot") {
      dispatch(setActiveChatToolId("knowledge_bot"));
    } else {
      dispatch(setActiveChatToolId(null));
    }
    const payload = {
      sessionId,
      userEmail,
      token,
      client,
    };
    dispatch(getUserChats(payload));
  };

  const categorizedData = categorizeData(filtered);
  const groups = buildSessionGroups(categorizedData);
  const hasAnySessions = groups.some((group) => group.items?.length > 0);
  const showLoadFailed = Boolean(sessionsError) && !sessionsLoading;

  return (
    <React.Fragment>
      {renameLoader && <Loader />}
      {isInitialSessionsLoad ? (
        <HistorySessionListSkeleton />
      ) : (
        <ErrorBoundary
          fallback={({ reset }) => (
            <SessionsLoadError
              message="Chat history could not be displayed."
              onRetry={reset}
            />
          )}
        >
          {!hasAnySessions && !showLoadFailed && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 1, py: 2 }}
            >
              No chat history yet.
            </Typography>
          )}
          {groups.map((group) => (
            <SessionGroup
              key={group.title}
              {...group}
              activeSessionId={activeSessionId}
              onSelect={handleuserSessionId}
              onMenuOpen={handleClick}
              menuAnchorEl={anchorEl}
              menuSessionId={menuSessionId}
              onMenuClose={handleClose}
              onRename={handleRenameDialog}
              onDelete={handleDeleteDialog}
            />
          ))}
          {showLoadFailed && (
            <SessionsLoadError
              message={
                hasAnySessions
                  ? "Couldn't load more chat history."
                  : "Chat history couldn't be loaded."
              }
              onRetry={() => fetchSessions({ minRows, maxRows })}
            />
          )}
          {!showLoadFailed && combinedSessions?.length > 0 && hasMoreSessions && (
            <Button
              onClick={() => {
                dispatch(incrementNoOfDays());
              }}
              disabled={isLoadingMoreSessions}
            >
              more
            </Button>
          )}
          {isLoadingMoreSessions && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
              <CircularProgress size={22} sx={{ color: "#E56800" }} />
            </Box>
          )}
        </ErrorBoundary>
      )}
      <RenameDialog
        open={renameDialogOpen}
        handleClose={handleRenameDialogClose}
        name={renameVal}
        userSessionId={userSessionId}
        handleRenamVal={handleRenamVal}
      />
      <DeleteDialog
        open={deleteDialogOpen}
        handleClose={handleDeleteDialogClose}
        dispatchAction={dispatchDeleteProcess}
        name={userSessionName}
      />
    </React.Fragment>
  );
}
